import PortalShell from "../../components/PortalShell";
import { useAuth } from "../../lib/auth";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, User2, TrendingUp, TrendingDown, Minus, Star, CalendarDays, BookOpen, AlertCircle } from "lucide-react";

const GRADE_COLOUR = { "9":"#15803d","8":"#16a34a","7":"#65a30d","6":"#ca8a04","5":"#ea580c","4":"#dc2626","3":"#b91c1c","2":"#991b1b","1":"#7f1d1d","U":"#64748b" };

function TrendBadge({ direction }) {
  if (direction === "up")     return <span className="flex items-center gap-1 text-xs text-green-600 font-semibold"><TrendingUp size={13} /> Improving</span>;
  if (direction === "down")   return <span className="flex items-center gap-1 text-xs text-red-600 font-semibold"><TrendingDown size={13} /> Declining</span>;
  return <span className="flex items-center gap-1 text-xs text-slate-500"><Minus size={13} /> Steady</span>;
}

function AccBar({ accuracy }) {
  const colour = accuracy >= 80 ? "#16a34a" : accuracy >= 60 ? "#d97706" : "#dc2626";
  return (
    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
      <div className="h-full rounded-full" style={{ width: `${accuracy}%`, background: colour }} />
    </div>
  );
}

function ChildCard({ data }) {
  const { student, kpis, recent_quizzes, topic_breakdown, next_lesson } = data;
  const gradeColour = GRADE_COLOUR[kpis.predicted_grade] || "#64748b";
  const accColour = kpis.accuracy >= 80 ? "#16a34a" : kpis.accuracy >= 60 ? "#d97706" : "#dc2626";

  const dueTopics = topic_breakdown.filter(t => t.accuracy >= 80);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="bg-[#0A1128] px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white font-bold text-lg">
            {student.name[0]}
          </div>
          <div>
            <div className="text-white font-semibold text-lg">{student.name}</div>
            <div className="text-white/50 text-xs">Year {student.year} · {student.tier || "—"} · {student.board || "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold" style={{ color: accColour }}>{kpis.accuracy}%</div>
            <div className="text-white/50 text-xs uppercase tracking-wider mt-0.5">Accuracy</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold" style={{ color: gradeColour }}>Grade {kpis.predicted_grade}</div>
            <div className="text-white/50 text-xs uppercase tracking-wider mt-0.5">Predicted</div>
          </div>
          <div className="text-right">
            <TrendBadge direction={kpis.trend} />
            <div className="text-white/40 text-xs mt-1">{kpis.total_attempts} quizzes total</div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-0 divide-x divide-slate-100">
        {/* Recent quizzes */}
        <div className="p-5">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Recent Activity</h4>
          {recent_quizzes.length === 0 ? (
            <p className="text-sm text-slate-400">No quiz attempts yet.</p>
          ) : (
            <div className="space-y-2">
              {recent_quizzes.map((q, i) => {
                const c = q.pct >= 80 ? "#16a34a" : q.pct >= 60 ? "#d97706" : "#dc2626";
                return (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div>
                      <div className="text-slate-700 font-medium">{q.topic}</div>
                      <div className="text-xs text-slate-400">{q.date}</div>
                    </div>
                    <span className="font-bold text-sm" style={{ color: c }}>{q.pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Topic breakdown */}
        <div className="p-5">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Topic Breakdown</h4>
          {topic_breakdown.length === 0 ? (
            <p className="text-sm text-slate-400">No topic data yet.</p>
          ) : (
            <div className="space-y-3">
              {[...topic_breakdown].reverse().slice(0, 6).map(t => (
                <div key={t.topic}>
                  <div className="flex justify-between text-xs mb-0.5">
                    <span className="text-slate-600 font-medium truncate">{t.topic}</span>
                    <span className="font-bold ml-2 shrink-0" style={{ color: t.accuracy >= 80 ? "#16a34a" : t.accuracy >= 60 ? "#d97706" : "#dc2626" }}>
                      {t.accuracy}%
                    </span>
                  </div>
                  <AccBar accuracy={t.accuracy} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Next lesson + grade info */}
        <div className="p-5 space-y-4">
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Next Lesson</h4>
            {next_lesson ? (
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <div className="flex items-center gap-1.5 text-xs text-[#D4AF37] font-semibold mb-1">
                  <CalendarDays size={11} />{next_lesson.date}
                </div>
                <div className="text-slate-800 font-medium text-sm">{next_lesson.time_slot}</div>
                <div className="text-xs text-slate-500">{next_lesson.lesson_type}</div>
              </div>
            ) : (
              <p className="text-sm text-slate-400">No upcoming lessons booked.</p>
            )}
          </div>

          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Grade Prediction</h4>
            <div className="rounded-lg p-3 border text-center" style={{ borderColor: gradeColour + "40", background: gradeColour + "08" }}>
              <div className="text-3xl font-bold mb-0.5" style={{ color: gradeColour }}>Grade {kpis.predicted_grade}</div>
              <div className="text-xs text-slate-500">based on {kpis.accuracy}% overall accuracy</div>
            </div>
          </div>

          {topic_breakdown.some(t => t.accuracy < 60) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-xs text-red-700 font-semibold mb-1">
                <AlertCircle size={11} /> Topics needing focus
              </div>
              <div className="flex flex-wrap gap-1">
                {topic_breakdown.filter(t => t.accuracy < 60).map(t => (
                  <span key={t.topic} className="text-[10px] bg-white border border-red-200 text-red-600 px-1.5 py-0.5 rounded-full">{t.topic}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ParentDashboard() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/analytics/children")
      .then(r => setChildren(r.data.children || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <PortalShell role="parent" title={`Welcome, ${user?.name?.split(" ")[0] || "Parent"}.`} subtitle="Your child's maths journey — live, transparent, actionable.">
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-slate-400" /></div>
      ) : children.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <h3 className="font-serif text-2xl brand-navy mb-3">No linked children yet</h3>
          <p className="text-slate-600 mb-6">Ask your child to add your email to their profile — or contact our office and we'll link them for you.</p>
          <a href="mailto:hello@geniuseducation.co.uk" className="btn-gold px-6 py-3 rounded-md inline-block">Email the office</a>
        </div>
      ) : (
        <div className="space-y-6">
          {children.map(c => <ChildCard key={c.student.id} data={c} />)}
        </div>
      )}
    </PortalShell>
  );
}
